import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  StackProps,
  Text,
} from '@chakra-ui/react';
import { createAgencyValidations } from '@components/crm/lead/shared/validations';
import Body2 from '@components/typography/Body2';
import Header from '@components/typography/Header';
import Link from '@components/typography/Link';
import { agencySignUp } from '@lib/requests';
import { useQueryClient } from '@tanstack/react-query';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import SuccessModal from './SuccessModal';

const SignUpForm = (props: StackProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(false);

  const schema = Yup.object().shape(createAgencyValidations);

  const queryClient = useQueryClient();

  const handleSignUp = async (values) => {
    setIsSubmitting(true);
    try {
      const res = await queryClient.fetchQuery({
        queryKey: ['agencySignUp'],
        queryFn: async () =>
          agencySignUp({
            agency: { displayName: values.agencyName, defaultEmail: values.email },
            agent: { email: values.email, first_name: values.firstName, last_name: values.lastName },
          }),
      });
      setIsSubmitting(false);
      if (res.status === 200) setModalOpen(true);
    } catch (err) {
      setIsSubmitting(false);
      setRateLimitError(true);
    }
  };

  return (
    <Stack {...props} bg={{ base: 'white', sm: 'bg.surface' }} boxShadow="2xl" borderRadius="xl">
      <Stack spacing="6">
        <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
          <Header as="h2" size={{ base: 'xs', md: 'sm' }} mt={5} mb={5}>
            Create your{' '}
            <Text display="inline" color="primary.500">
              free
            </Text>{' '}
            account
          </Header>
        </Stack>
      </Stack>
      <Formik
        initialValues={{ email: '', firstName: '', lastName: '', agencyName: '' }}
        validationSchema={schema}
        onSubmit={async (values, { resetForm, validateForm }) => {
          validateForm();
          await handleSignUp(values);
          resetForm();
        }}
      >
        {({ errors }) => (
          <Form>
            <Stack spacing="7">
              <HStack spacing="4">
                <Field name="firstName">
                  {({ field, form }) => (
                    <>
                      <FormControl isInvalid={form.errors.firstName && form.touched.firstName}>
                        <FormLabel htmlFor="firstName">First Name</FormLabel>
                        <FormErrorMessage hidden={form.errors.firstName}>{form.errors.firstName}</FormErrorMessage>
                        <Input
                          focusBorderColor="primary.500"
                          borderColor={form.errors.firstName && form.touched.firstName ? 'red.500' : null}
                          name="firstName"
                          {...field}
                        />
                      </FormControl>
                    </>
                  )}
                </Field>
                <Field name="lastName">
                  {({ field, form }) => (
                    <>
                      <FormControl isInvalid={form.errors.lastName && form.touched.lastName}>
                        <FormLabel htmlFor="lastName">Last Name</FormLabel>
                        <Input
                          focusBorderColor="primary.500"
                          borderColor={form.errors.lastName && form.touched.lastName ? 'red.500' : null}
                          name="lastName"
                          {...field}
                        />
                      </FormControl>
                    </>
                  )}
                </Field>
              </HStack>
              <Field name="email">
                {({ field, form }) => (
                  <>
                    <FormControl isInvalid={form.errors.email && form.touched.email}>
                      <FormLabel htmlFor="email">Email</FormLabel>
                      <Input
                        focusBorderColor="primary.500"
                        borderColor={form.errors.email && form.touched.email ? 'red.500' : null}
                        type="email"
                        name="email"
                        {...field}
                      />
                    </FormControl>
                  </>
                )}
              </Field>
              <Field name="agencyName">
                {({ field, form }) => (
                  <>
                    <FormControl isInvalid={form.errors.agencyName && form.touched.agencyName}>
                      <FormLabel htmlFor="agencyName">Agency Name</FormLabel>
                      <Input
                        focusBorderColor="primary.500"
                        borderColor={form.errors.email && form.touched.email ? 'red.500' : null}
                        name="agencyName"
                        {...field}
                      />
                    </FormControl>
                  </>
                )}
              </Field>
            </Stack>

            <Button
              isDisabled={!!Object.keys(errors)?.length}
              colorScheme="primary"
              type="submit"
              w="full"
              mt={10}
              isLoading={isSubmitting}
            >
              Sign Up
            </Button>
            <Body2 hidden={!rateLimitError} color="red.500">
              Something went wrong. Please contact us at info@bedrockmedicare.com.
            </Body2>
            <Body2 color="fg.muted" textAlign="center" mt={5} mb={5}>
              Already have an account?{' '}
              <Link fontSize={[14, 16, 16]} href="/">
                Log In
              </Link>
            </Body2>
          </Form>
        )}
      </Formik>
      <SuccessModal isOpen={modalOpen} setIsOpen={setModalOpen} />
    </Stack>
  );
};

export default SignUpForm;
